// src/auth/authService.js

// Other imports and WebAuth declaration..

import { CognitoAuth } from 'amazon-cognito-auth-js';
import EventEmitter from 'events';
import { useAuthStore } from '../stores/auth';
import { generateRandomState, generateRandomNonce, isJwtExpired } from '../util/oauth-utils';
import { createEncodedState } from '../util/state-utils';

const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID;
const APP_DOMAIN = import.meta.env.VITE_COGNITO_APP_DOMAIN;
const REDIRECT_URI = window.location.protocol + '//' + window.location.host + '/callback';
const USERPOOL_ID = import.meta.env.VITE_COGNITO_USERPOOL_ID;
const REDIRECT_URI_SIGNOUT = window.location.protocol + '//' + window.location.host + '/logout/';

var authData = {
  ClientId: CLIENT_ID,
  AppWebDomain: APP_DOMAIN,
  TokenScopesArray: ['openid', 'email', 'profile'],
  RedirectUriSignIn: REDIRECT_URI,
  RedirectUriSignOut: REDIRECT_URI_SIGNOUT,
  UserPoolId: USERPOOL_ID
};

const localStorageKey = 'loggedIn';
const loginEvent = 'loginEvent';

var auth = new CognitoAuth(authData);

// ULC-3361: parseCognitoWebResponse reports parse/state failures only through
// userhandler.onFailure, and on failure it returns without touching the session
// — leaving whatever the SDK restored from its localStorage cache in place.
// Record the reason so parseCallbackAccessToken can surface it instead of
// silently accepting a stale cached session.
let lastParseFailure = null;

class AuthService extends EventEmitter {
  idToken = null;
  tokenExpiry = null;
  accessToken = null;

  // Starts the user login flow
  login(options = {}) {
    // Handle both old string state format and new options object format
    let state = generateRandomState();

    if (options && typeof options.state === 'string') {
      state = options.state;
    } else if (options && typeof options === 'object' && Object.keys(options).length > 0) {
      // Convert options object using state utils
      state = createEncodedState(options);
    }

    const nonce = generateRandomNonce();

    let authUrl = `https://${APP_DOMAIN}/oauth2/authorize?identity_provider=asurite-shibboleth&redirect_uri=${REDIRECT_URI}&response_type=token&client_id=${CLIENT_ID}&scope=email%20openid%20profile&state=${encodeURIComponent(
      state
    )}&nonce=${encodeURIComponent(nonce)}`;

    auth.launchUri(authUrl);
  }

  getSignInUserSession() {
    return auth.getSignInUserSession();
  }

  getAppWebDomain() {
    return auth.getAppWebDomain();
  }

  // Handles the callback request from Auth0
  async parseCallbackAccessToken() {
    lastParseFailure = null;
    auth.parseCognitoWebResponse(window.location.href);

    const session = auth.getSignInUserSession();

    // ULC-3361: a failed parse leaves the SDK's cached session in place, and its
    // access token is truthy even when it is days old. Accepting it here stores a
    // dead ID token and every later aether exchange fails as EXPIRED — so reject
    // any reported parse failure outright (a still-valid cached token must not
    // mask it), and additionally require a non-expired ID token for the silent
    // no-op case where the SDK reports nothing.
    const idTokenJwt =
      typeof session?.getIdToken === 'function'
        ? session.getIdToken()?.getJwtToken?.() || session.getIdToken()?.jwtToken
        : session?.idToken?.jwtToken;
    if (lastParseFailure || !idTokenJwt || isJwtExpired(idTokenJwt)) {
      const reason = typeof lastParseFailure === 'string' ? lastParseFailure : lastParseFailure?.message;
      const err = new Error(reason ? `Cognito callback parse failed: ${reason}` : 'Invalid session');
      // Callback.vue matches errorDescription for known Cognito failure copy
      // (e.g. the ASURITE-affiliation-not-ready message).
      if (reason) err.errorDescription = reason;
      throw err;
    }

    if (session.getAccessToken().jwtToken) {
      this.localLogin(session);

      return session.getAccessToken().getJwtToken();
    }

    throw new Error('Invalid session');
  }

  localLogin(authResult) {
    this.accessToken = authResult.accessToken.jwtToken;
    this.profile = authResult.getIdToken().decodePayload();
    this.idToken = authResult.idToken;

    // Convert the JWT expiry time from seconds to milliseconds. ULC-3361: this
    // was auth_time (when the user authenticated — always in the past); only
    // truthiness-checked today, but exp is what the name promises.
    this.tokenExpiry = new Date(this.profile.exp * 1000);

    localStorage.setItem(localStorageKey, 'true');

    this.emit(loginEvent, {
      loggedIn: true,
      authResult,
      state: authResult.state || {}
    });

    // Update Pinia store
    const authStore = useAuthStore();
    authStore.setAuth(true);
  }

  silentAuth() {
    return new Promise((resolve, reject) => {
      const session = auth.getSignInUserSession();

      if (session.getAccessToken().jwtToken) {
        this.localLogin(session);

        resolve(session);
      } else {
        reject();
      }
    });
  }

  getUserInfoStorageKey() {
    var keyPrefix = 'CognitoIdentityServiceProvider.' + auth.getClientId();
    var tokenUserName = auth.signInUserSession.getAccessToken().getUsername();
    var userInfoKey = keyPrefix + '.' + tokenUserName + '.userInfo';

    return userInfoKey;
  }

  logOut() {
    // Synchronously clear the Cognito SDK's own localStorage keys before the
    // async CAS-popup + setTimeout(auth.signOut()) below. If the tab closes
    // or the popup blocker kills the CAS window within that 1s window, the
    // SDK keys would survive and the next login silently restores a stale
    // session — bypassing the OTP-prompt path entirely.
    // Keys look like `CognitoIdentityServiceProvider.<clientId>.<username>.*`.
    const cognitoPrefix = `CognitoIdentityServiceProvider.${auth.getClientId()}.`;
    const staleKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(cognitoPrefix)) staleKeys.push(key);
    }
    staleKeys.forEach((key) => localStorage.removeItem(key));

    localStorage.removeItem(localStorageKey);

    this.idToken = null;
    this.tokenExpiry = null;
    this.profile = null;

    window.open(`https://weblogin.asu.edu/cas/logout`, '_blank');

    setTimeout(() => {
      auth.signOut();
    }, 1000);

    this.emit(loginEvent, {
      loggedIn: false
    });
  }

  isAuthenticated() {
    return this.tokenExpiry && localStorage.getItem(localStorageKey) === 'true';
  }

  // ULC-3361: the aether exchange needs the current ID token as a raw JWT. The
  // in-memory `idToken` field dies on every page load, but the Cognito SDK
  // restores the session from its own localStorage cache at construction — so
  // read the live session first and keep the field only as a fallback. Prefer a
  // non-expired candidate; when everything is expired, still return it so the
  // caller raises the re-authable EXPIRED state instead of NO_ID_TOKEN.
  getIdToken() {
    const session = auth.getSignInUserSession();
    const sessionIdToken = typeof session?.getIdToken === 'function' ? session.getIdToken() : null;
    const sessionJwt =
      (typeof sessionIdToken?.getJwtToken === 'function' && sessionIdToken.getJwtToken()) ||
      sessionIdToken?.jwtToken ||
      null;
    const fieldJwt =
      (typeof this.idToken === 'string' && this.idToken) ||
      (typeof this.idToken?.getJwtToken === 'function' && this.idToken.getJwtToken()) ||
      this.idToken?.jwtToken ||
      null;

    const candidates = [sessionJwt, fieldJwt].filter(Boolean);
    return candidates.find((jwt) => !isJwtExpired(jwt)) || candidates[0] || null;
  }

  getAsuRiteIdFromToken() {
    return (this.profile || {})['custom:asuriteid'];
  }

  getAsurite() {
    return (this.profile || {})['custom:asuriteid'] || import.meta.env.VITE_ASURITE_DUMMY;
  }
}

// ULC-3361: onFailure must not be a no-op — parseCognitoWebResponse reports a
// Cognito error response (e.g. bad affiliation, mangled hash) ONLY through this
// callback, then leaves the stale cached session in place. Record the reason so
// parseCallbackAccessToken can reject the callback instead of logging in with a
// dead token.
auth.userhandler = {
  onSuccess: function () {},
  onFailure: function (err) {
    lastParseFailure = err || 'unknown Cognito parse failure';
  }
};

export default new AuthService();
